/**
 * PlaceSearchService integrates with place_service.proto for place search functionality.
 * Provides map-specific place operations with viewport-aware search.
 */

import { placeClient } from "@/api/grpc";
import { extractPlacesFromResult } from "@/lib/searchResults";
import { create } from "@bufbuild/protobuf";
import {
  GetPlaceDetailsRequestSchema,
  GetPlaceDetailsResponse,
  SearchPlacesRequestSchema,
  SearchPlacesResponse
} from "@generated/proto/service";

import {
  MapError,
  MapErrorCode,
} from "../types";
import {
  calculateDistance,
  calculateRadiusFromZoom,
} from "../utils/geoCalculations";

import type {
  LatLng,
  Place,
  PlaceSearch,
  PlaceType
} from '@generated/proto/model';
import type {
  GeoCornerBounds,
  MapViewport,
  PlaceSearchMarker
} from '../types';
export class PlaceSearchService {
  private searchCache = new Map<string, { response: SearchPlacesResponse; timestamp: number }>()
  private detailsCache = new Map<string, { response: GetPlaceDetailsResponse; timestamp: number }>()
  private readonly CACHE_TTL = 5 * 60 * 1000 // 5 minutes

  /**
   * Search places using place_service.proto integration.
   */
  async searchPlaces(search: PlaceSearch): Promise<SearchPlacesResponse> {
    try {
      const cacheKey = this.createSearchCacheKey(search)
      const cached = this.getCachedSearch(cacheKey)
      if (cached) return cached

      const request = create(SearchPlacesRequestSchema, {
        search,
        languageCode: 'en',
        regionCode: 'US'
      })

      const response = await placeClient.searchPlaces(request)

      // Cache successful response
      this.cacheSearchResponse(cacheKey, response)

      return response
    } catch (error) {
      throw new MapError(
        'Failed to search places',
        MapErrorCode.PLACE_SEARCH_ERROR,
        {
          query: search.query,
          error: error instanceof Error ? error.message : String(error)
        },
        true, // recoverable
        true  // retryable
      )
    }
  }

  /**
   * Get detailed place information by place ID.
   */
  async getPlaceDetails(placeId: string, fieldMask?: string[]): Promise<GetPlaceDetailsResponse> {
    try {
      const cacheKey = `details_${placeId}_${fieldMask?.join(',') || 'all'}`
      const cached = this.getCachedDetails(cacheKey)
      if (cached) return cached

      const request = create(GetPlaceDetailsRequestSchema, {
        placeId,
        fieldMask: fieldMask ? { paths: fieldMask } : undefined,
        languageCode: 'en',
        regionCode: 'US'
      })

      const response = await placeClient.getPlaceDetails(request)

      // Cache successful response
      this.cacheDetailsResponse(cacheKey, response)

      return response
    } catch (error) {
      throw new MapError(
        `Failed to get place details for ${placeId}`,
        MapErrorCode.PLACE_SEARCH_ERROR,
        {
          placeId,
          error: error instanceof Error ? error.message : String(error)
        },
        true, // recoverable
        true  // retryable
      )
    }
  }

  /**
   * Search places near the current viewport center.
   */
  async searchNearViewport(
    query: string,
    viewport: MapViewport,
    options?: {
      radiusMeters?: number
      placeType?: PlaceType
      maxResults?: number
      minRating?: number
      openNow?: boolean
    }
  ): Promise<Place[]> {
    const search = create(PlaceSearch, {
      query,
      location: viewport.center,
      radiusMeters: options?.radiusMeters || calculateRadiusFromZoom(viewport.zoom),
      placeType: options?.placeType,
      maxResults: options?.maxResults || 20,
      minRating: options?.minRating,
      openNow: options?.openNow
    })

    const response = await this.searchPlaces(search)

    // Extract places from SearchSession results
    const places = extractPlacesFromResult(response)

    return places
  }

  /**
   * Get places within specific bounds.
   */
  async getPlacesInBounds(
    bounds: GeoCornerBounds,
    options?: {
      query?: string
      placeType?: PlaceType
      maxResults?: number
      minRating?: number
    }
  ): Promise<Place[]> {
    const center = this.calculateBoundsCenter(bounds)
    const radiusMeters = this.calculateRadiusFromBounds(bounds)

    const search = create(PlaceSearch, {
      query: options?.query || '',
      location: center,
      radiusMeters: Math.min(radiusMeters, 50000), // Cap at 50km
      placeType: options?.placeType,
      maxResults: options?.maxResults || 50,
      minRating: options?.minRating
    })

    const response = await this.searchPlaces(search)

    // Extract places from SearchSession results
    const allPlaces = extractPlacesFromResult(response)

    // Filter results to only include places actually within bounds
    const placesInBounds = allPlaces.filter(place =>
      place.latlng && this.isLocationInBounds(place.latlng, bounds)
    )

    return placesInBounds
  }

  /**
   * Search for specific place types near a location.
   */
  async searchByType(
    placeType: PlaceType,
    center: LatLng,
    radiusMeters: number = 5000,
    options?: {
      maxResults?: number
      minRating?: number
      openNow?: boolean
    }
  ): Promise<Place[]> {
    const search = create(PlaceSearch, {
      query: '', // Empty query, search by type only
      location: center,
      radiusMeters,
      placeType,
      maxResults: options?.maxResults || 20,
      minRating: options?.minRating,
      openNow: options?.openNow
    })

    const response = await this.searchPlaces(search)

    // Extract places from SearchSession results
    const places = extractPlacesFromResult(response)

    return places
  }

  /**
   * Create PlaceSearchMarkers from Place objects.
   */
  createPlaceSearchMarkers(places: Place[]): PlaceSearchMarker[] {
    return places
      .filter(place => place.latlng)
      .map(place => ({
        id: `place_${place.id}`,
        location: { latitude: place.latlng!.lat, longitude: place.latlng!.lng },
        title: place.name,
        place,
        icon: this.getPlaceTypeIcon(place.type),
        data: place
      }))
  }

  /**
   * Get autocomplete suggestions based on partial query.
   */
  async getAutocompleteSuggestions(
    partialQuery: string,
    viewport: MapViewport,
    options?: {
      maxSuggestions?: number
      placeTypes?: PlaceType[]
    }
  ): Promise<string[]> {
    // Simple implementation - could be enhanced with autocomplete service
    if (partialQuery.length < 2) return []

    try {
      const places = await this.searchNearViewport(partialQuery, viewport, {
        maxResults: options?.maxSuggestions || 5
      })

      return places
        .map(place => place.name)
        .filter(name => name.toLowerCase().includes(partialQuery.toLowerCase()))
        .slice(0, options?.maxSuggestions || 5)
    } catch (error) {
      console.warn('Autocomplete suggestions failed:', error)
      return []
    }
  }

  /**
   * Clear search cache to free memory.
   */
  clearCache(): void {
    this.searchCache.clear()
    this.detailsCache.clear()
  }

  /**
   * Get cache statistics for debugging.
   */
  getCacheStats(): { searchEntries: number; detailsEntries: number } {
    return {
      searchEntries: this.searchCache.size,
      detailsEntries: this.detailsCache.size
    }
  }

  /**
   * Create cache key for search request.
   */
  private createSearchCacheKey(search: PlaceSearch): string {
    const parts = [
      search.query || 'empty',
      search.location?.lat.toString() || '0',
      search.location?.lng.toString() || '0',
      search.radiusMeters?.toString() || '5000',
      search.placeType?.toString() || 'any',
      search.maxResults?.toString() || '20',
      search.minRating?.toString() || '0',
      search.openNow ? 'open' : 'any'
    ]
    return parts.join('|')
  }

  /**
   * Get cached search response if still valid.
   */
  private getCachedSearch(cacheKey: string): SearchPlacesResponse | null {
    const cached = this.searchCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.response
    }
    if (cached) {
      this.searchCache.delete(cacheKey)
    }
    return null
  }

  /**
   * Cache search response.
   */
  private cacheSearchResponse(cacheKey: string, response: SearchPlacesResponse): void {
    this.searchCache.set(cacheKey, {
      response,
      timestamp: Date.now()
    })

    // Prevent memory leaks by limiting cache size
    if (this.searchCache.size > 100) {
      const firstKey = this.searchCache.keys().next().value
      this.searchCache.delete(firstKey)
    }
  }

  /**
   * Get cached place details if still valid.
   */
  private getCachedDetails(cacheKey: string): GetPlaceDetailsResponse | null {
    const cached = this.detailsCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.response
    }
    if (cached) {
      this.detailsCache.delete(cacheKey)
    }
    return null
  }

  /**
   * Cache place details response.
   */
  private cacheDetailsResponse(cacheKey: string, response: GetPlaceDetailsResponse): void {
    this.detailsCache.set(cacheKey, {
      response,
      timestamp: Date.now()
    })

    // Prevent memory leaks
    if (this.detailsCache.size > 50) {
      const firstKey = this.detailsCache.keys().next().value
      this.detailsCache.delete(firstKey)
    }
  }


  /**
   * Calculate center point of bounds.
   */
  private calculateBoundsCenter(bounds: GeoCornerBounds): LatLng {
    return {
      lat: (bounds.northeast.lat + bounds.southwest.lat) / 2,
      lng: (bounds.northeast.lng + bounds.southwest.lng) / 2
    }
  }

  /**
   * Calculate radius that encompasses the bounds.
   */
  private calculateRadiusFromBounds(bounds: GeoCornerBounds): number {
    const center = this.calculateBoundsCenter(bounds)

    // Calculate distance to farthest corner
    const distances = [
      calculateDistance(center, bounds.northeast),
      calculateDistance(center, bounds.southwest),
      calculateDistance(center, { lat: bounds.northeast.lat, lng: bounds.southwest.lng }),
      calculateDistance(center, { lat: bounds.southwest.lat, lng: bounds.northeast.lng })
    ]

    return Math.max(...distances)
  }

  /**
   * Check if location is within bounds.
   */
  private isLocationInBounds(location: LatLng, bounds: GeoCornerBounds): boolean {
    return (
      location.lat >= bounds.southwest.lat &&
      location.lat <= bounds.northeast.lat &&
      location.lng >= bounds.southwest.lng &&
      location.lng <= bounds.northeast.lng
    )
  }

  /**
   * Get appropriate icon for place type.
   */
  private getPlaceTypeIcon(placeType?: PlaceType): string {
    if (!placeType) return '📍'

    const iconMap: Record<string, string> = {
      'RESTAURANT': '🍽️',
      'LODGING': '🏨',
      'TOURIST_ATTRACTION': '🎯',
      'GAS_STATION': '⛽',
      'HOSPITAL': '🏥',
      'PHARMACY': '💊',
      'SCHOOL': '🏫',
      'BANK': '🏦',
      'SHOPPING_MALL': '🛍️',
      'GROCERY_OR_SUPERMARKET': '🛒',
      'ATM': '💳',
      'BEAUTY_SALON': '💄',
      'BOOK_STORE': '📚',
      'CAFE': '☕',
      'CAR_DEALER': '🚗',
      'CAR_RENTAL': '🚙',
      'CAR_REPAIR': '🔧',
      'CAR_WASH': '🚿',
      'CASINO': '🎰',
      'CEMETERY': '⚰️',
      'CHURCH': '⛪',
      'CITY_HALL': '🏛️',
      'CLOTHING_STORE': '👕',
      'CONVENIENCE_STORE': '🏪',
      'DENTIST': '🦷',
      'DEPARTMENT_STORE': '🏬',
      'ELECTRONICS_STORE': '📱',
      'EMBASSY': '🏛️',
      'FIRE_STATION': '🚒',
      'FLORIST': '🌸',
      'FUNERAL_HOME': '⚱️',
      'FURNITURE_STORE': '🪑',
      'GYM': '🏋️',
      'HARDWARE_STORE': '🔨',
      'HOME_GOODS_STORE': '🏠',
      'JEWELRY_STORE': '💎',
      'LAUNDRY': '👕',
      'LAWYER': '⚖️',
      'LIBRARY': '📖',
      'LIQUOR_STORE': '🍷',
      'LOCKSMITH': '🔑',
      'MEAL_DELIVERY': '🚚',
      'MEAL_TAKEAWAY': '🥡',
      'MOVIE_THEATER': '🎬',
      'MOVING_COMPANY': '📦',
      'MUSEUM': '🏛️',
      'NIGHT_CLUB': '🌙',
      'PAINTER': '🎨',
      'PARK': '🌳',
      'PARKING': '🅿️',
      'PET_STORE': '🐕',
      'PHYSIOTHERAPIST': '🏥',
      'PLUMBER': '🔧',
      'POLICE': '👮',
      'POST_OFFICE': '📮',
      'REAL_ESTATE_AGENCY': '🏠',
      'ROOFING_CONTRACTOR': '🏠',
      'RV_PARK': '🚐',
      'SPA': '💆',
      'STADIUM': '🏟️',
      'STORAGE': '📦',
      'STORE': '🛍️',
      'SUBWAY_STATION': '🚇',
      'SUPERMARKET': '🛒',
      'TAXI_STAND': '🚕',
      'TRAIN_STATION': '🚂',
      'TRANSIT_STATION': '🚌',
      'TRAVEL_AGENCY': '✈️',
      'UNIVERSITY': '🎓',
      'VETERINARY_CARE': '🐾',
      'ZOO': '🦁'
    }

    return iconMap[String(placeType)] || '📍'
  }

}